import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { usePublicSettings } from "@/lib/queries";
import { BobaLogo } from "./BobaLogo";
import { Menu as MenuIcon, X, ShoppingBag, MapPin } from "lucide-react";

const ALL_NAV_ITEMS = [
  { to: "/order", label: "Order", requiresOrdering: true },
  { to: "/events", label: "Events" },
  { to: "/rewards", label: "Rewards" },
  { to: "/contact", label: "Contact" },
];

export function PublicNav() {
  const { customer, user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  // `data` is undefined on first paint — assume ordering is on so we don't
  // flash "Find us here" then snap back to "Order online" for the typical
  // owner who keeps ordering live.
  const { data: pubSettings } = usePublicSettings();
  const orderingEnabled = pubSettings?.online_ordering_enabled !== false;
  const navItems = ALL_NAV_ITEMS.filter((it) => !it.requiresOrdering || orderingEnabled);

  // Close drawer on route change
  useEffect(() => { setOpen(false); }, [location.pathname]);

  // Lock scroll when drawer open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <header data-testid="public-nav" className="sticky top-0 z-40 bp-glass border-b">
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-2 px-3 md:px-6 py-2.5 md:py-3">
        {/* Logo + brand on mobile so it's clear who we are even when small */}
        <Link
          to="/"
          className="flex items-center gap-2 shrink-0 min-w-0"
          data-testid="nav-logo"
          aria-label="The Boba Place — Home"
        >
          <BobaLogo className="h-9 md:h-10 w-auto" />
          <span className="md:hidden font-brand font-semibold text-base leading-none truncate">
            The Boba Place
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
          {navItems.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              data-testid={`nav-${it.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-3 py-2 text-sm rounded-lg hover:bg-muted transition ${isActive ? "text-primary font-medium" : "text-foreground/80"}`
              }
            >
              {it.label}
            </NavLink>
          ))}
        </nav>

        {/* Right side: Order CTA (or "Find us here" when ordering is paused) */}
        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
          {orderingEnabled ? (
            <Link
              to="/order"
              data-testid="nav-order-cta"
              aria-label="Order online"
              className="inline-flex items-center gap-1.5 rounded-2xl px-3 sm:px-4 py-2 text-xs sm:text-sm font-bold text-white shadow-md hover:shadow-lg transition whitespace-nowrap"
              style={{ background: "linear-gradient(135deg, hsl(252 63% 54%), hsl(330 78% 60%))" }}
            >
              <ShoppingBag className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              Order
              <span className="hidden md:inline">online</span>
            </Link>
          ) : (
            <Link
              to="/events"
              data-testid="nav-find-us-cta"
              aria-label="Find us at an upcoming event"
              className="inline-flex items-center gap-1.5 rounded-2xl px-3 sm:px-4 py-2 text-xs sm:text-sm font-bold text-white shadow-md hover:shadow-lg transition whitespace-nowrap"
              style={{ background: "linear-gradient(135deg, hsl(252 63% 54%), hsl(330 78% 60%))" }}
            >
              <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              Find us here
            </Link>
          )}

          {customer ? (
            <Link to="/rewards" className="text-sm hidden md:block" data-testid="nav-customer-name">
              Hi, {customer.name?.split(" ")[0]} · {customer.points} pts
            </Link>
          ) : null}

          {user ? (
            <Link
              to="/admin"
              className="bp-btn-primary text-xs sm:text-sm hidden sm:inline-flex"
              data-testid="nav-admin-link"
            >
              Admin
            </Link>
          ) : customer ? (
            <button
              onClick={logout}
              className="bp-chip bp-focus-ring hidden md:inline-flex"
              data-testid="nav-logout"
            >
              Log out
            </button>
          ) : (
            <Link
              to="/login"
              className="bp-chip bp-focus-ring hidden md:inline-flex"
              data-testid="nav-login"
            >
              Log in
            </Link>
          )}

          {/* Mobile burger — bigger tap target, visible label-pairing for clarity */}
          <button
            onClick={() => setOpen(true)}
            aria-label="Open menu"
            aria-expanded={open}
            aria-controls="mobile-drawer"
            className="lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-xl border border-border bg-card hover:bg-muted active:scale-95 transition focus-visible:ring-2 focus-visible:ring-ring"
            data-testid="nav-burger"
          >
            <MenuIcon className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50" role="dialog" aria-modal="true" id="mobile-drawer">
          <button
            aria-label="Close menu overlay"
            className="absolute inset-0 bg-night/40 backdrop-blur-sm"
            onClick={() => setOpen(false)}
          />
          <div className="absolute right-0 top-0 h-full w-80 max-w-[88vw] bg-card shadow-2xl flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <Link to="/" onClick={() => setOpen(false)} className="flex items-center gap-2">
                <BobaLogo className="h-9 w-auto" />
                <span className="font-brand font-semibold text-base leading-none">The Boba Place</span>
              </Link>
              <button
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                className="p-2 rounded-lg hover:bg-muted"
                data-testid="nav-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <nav className="flex-1 p-2 overflow-y-auto" aria-label="Mobile primary">
              {navItems.map((it) => (
                <NavLink
                  key={it.to}
                  to={it.to}
                  data-testid={`mnav-${it.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `block px-4 py-3.5 rounded-xl text-base ${isActive ? "bg-primary/10 text-primary font-medium" : "hover:bg-muted"}`
                  }
                >
                  {it.label}
                </NavLink>
              ))}
              {customer && (
                <div className="mt-3 px-4 py-3 rounded-xl bg-primary/5 border border-primary/20" data-testid="mnav-rewards-summary">
                  <p className="text-xs uppercase tracking-wider text-primary font-semibold">Rewards</p>
                  <p className="font-display font-semibold mt-0.5">
                    {customer.name?.split(" ")[0]} · {customer.points} pts
                  </p>
                </div>
              )}
            </nav>
            <div className="border-t p-3 flex flex-col gap-2">
              <Link
                to="/order"
                onClick={() => setOpen(false)}
                data-testid="mnav-order-cta"
                className="rounded-2xl py-3 text-center font-bold text-white shadow-md inline-flex items-center justify-center gap-2"
                style={{ background: "linear-gradient(135deg, hsl(252 63% 54%), hsl(330 78% 60%))" }}
              >
                <ShoppingBag className="w-4 h-4" /> Order online
              </Link>
              {user ? (
                <Link to="/admin" onClick={() => setOpen(false)} className="bp-chip bg-muted justify-center py-2.5">
                  Admin dashboard
                </Link>
              ) : customer ? (
                <button onClick={() => { logout(); setOpen(false); }} className="bp-chip bg-muted justify-center py-2.5">
                  Log out
                </button>
              ) : (
                <div className="flex gap-2">
                  <Link to="/login" onClick={() => setOpen(false)} className="bp-chip bg-muted flex-1 justify-center py-2.5" data-testid="mnav-login">Log in</Link>
                  <Link to="/signup" onClick={() => setOpen(false)} className="bp-btn-primary flex-1 text-center" data-testid="mnav-signup">Join rewards</Link>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

export function PublicFooter() {
  return (
    <footer data-testid="public-footer" className="border-t mt-16 bg-muted/40">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 grid gap-8 sm:grid-cols-2 md:grid-cols-4">
        <div>
          <div className="flex items-center"><BobaLogo className="h-10 w-auto" /></div>
          <p className="text-sm text-muted-foreground mt-3">Fresh-brewed tea, chewy pearls, good vibes. Hailey · Ketchum · Sun Valley.</p>
        </div>
        <div>
          <h4 className="font-semibold mb-2">Explore</h4>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li><Link to="/order">Order online</Link></li>
            <li><Link to="/events">Events & Catering</Link></li>
            <li><Link to="/rewards">Rewards</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="font-semibold mb-2">Company</h4>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li><Link to="/about">About</Link></li>
            <li><Link to="/contact">Contact</Link></li>
            <li><Link to="/faq">FAQ</Link></li>
            <li><Link to="/gift-cards" data-testid="footer-gift-cards-link">Gift cards</Link></li>
            <li><Link to="/terms" data-testid="footer-terms-link">Terms &amp; Conditions</Link></li>
            <li><Link to="/privacy" data-testid="footer-privacy-link">Privacy Policy</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="font-semibold mb-2">Visit us</h4>
          <p className="text-sm text-muted-foreground">100 Main Street, Hailey, ID</p>
          <p className="text-sm text-muted-foreground">Mon–Sun · 11am–9pm</p>
        </div>
      </div>
      <div className="border-t mx-4 md:mx-6 pt-4 pb-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-muted-foreground">
        <span>© {new Date().getFullYear()} The Boba Place</span>
        <div className="flex items-center gap-4">
          <Link to="/terms" className="hover:text-foreground">Terms</Link>
          <Link to="/privacy" className="hover:text-foreground">Privacy</Link>
          <Link to="/contact" className="hover:text-foreground">Contact</Link>
        </div>
      </div>
    </footer>
  );
}
